import { useEffect, useRef } from 'react';

/**
 * Custom hook for managing project-specific CSS styles
 * Dynamically loads and unloads CSS files based on project selection
 */
export function useProjectStyles(selectedProject, projects) {
  const currentStyleLinkRef = useRef(null);

  useEffect(() => {
    // Remove previous project style if exists
    if (currentStyleLinkRef.current) {
      currentStyleLinkRef.current.remove();
      currentStyleLinkRef.current = null;
    }

    // If no project selected, keep default.css (already loaded)
    if (!selectedProject) {
      return;
    }

    // Find the selected project
    const project = projects.find(p => p.id === selectedProject);
    
    // If project not found or no styles defined, keep default.css
    if (!project || !project.styles) {
      return;
    }

    // Get CSS path from project configuration
    // Paths should be relative to public folder (e.g., "/styles/project.css")
    let cssPath = project.styles;
    
    // Ensure path starts with / for absolute paths from public folder
    if (!cssPath.startsWith('/') && !cssPath.startsWith('http')) {
      cssPath = '/' + cssPath;
    }

    // Create and append link element for project styles
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.type = 'text/css';
    link.href = cssPath;
    link.id = `project-style-${selectedProject}`;
    
    // Store reference for cleanup
    currentStyleLinkRef.current = link;
    
    // Append to head
    document.head.appendChild(link);

    // Cleanup function
    return () => {
      if (currentStyleLinkRef.current) {
        currentStyleLinkRef.current.remove();
        currentStyleLinkRef.current = null;
      }
    };
  }, [selectedProject, projects]);
}

